import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  addDoc,
  query,
  where,
  arrayUnion,
} from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';

export interface BankData {
  id: string;
  name: string;
  dateCreated?: any;
}

export interface BankClientData {
  id: string;
  bankId: string;
  branchId: string;
  bankName: string;
  branchCode?: string;
  mobile?: string;
  address?: string;
  dateOfRegistration?: Date | any;
  locationId?: string;
  locationName?: string;
  securityCode?: string;
  name?: string; // Bank name (same as bankName)
  type?: string; // 'bank'
  requiredGuard?: number;
  psoName?: string;
  psoEmail?: string;
  psoNumber?: string;
  bomName?: string;
  bomEmail?: string;
  bomNumber?: string;
  pinCode?: string;
  salary?: number;
  salaryHistory?: SalaryHistoryEntry[];
  otGuardCount?: number;
}

export interface SalaryHistoryEntry {
  amount: number;
  date: any;
  type: 'set' | 'increase' | 'decrease';
  previousAmount?: number;
}

export interface LocationData {
  id: string;
  name: string;
}

export const fetchLocations = async (): Promise<LocationData[]> => {
  try {
    const locationsSnap = await getDocs(collection(db, 'locations'));
    return locationsSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as LocationData[];
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [];
  }
};

export const fetchBanks = async (companyId?: string): Promise<BankData[]> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  try {
    const banksSnap = await getDocs(collection(db, 'bankClient', uid, 'banks'));
    return banksSnap.docs.map((doc) => ({
      id: doc.id,
      name: doc.data().name || '',
      ...doc.data(),
    })) as BankData[];
  } catch (error) {
    console.error('Error fetching banks:', error);
    throw error;
  }
};

export const createBank = async (name: string, companyId?: string): Promise<string> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  try {
    // Check if bank already exists
    const banksRef = collection(db, 'bankClient', uid, 'banks');
    const q = query(banksRef, where('name', '==', name));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].id;
    }

    const docRef = await addDoc(banksRef, {
      name,
      dateCreated: new Date(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating bank:', error);
    throw error;
  }
};

/**
 * Generate a pin code for a branch
 * Format: [Bank Initials][Branch Code][4 Random Digits]
 */
export const generatePinCode = (bankName: string, branchCode: string): string => {
  const initials = bankName
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
  const randomDigits = Math.floor(1000 + Math.random() * 9000);
  return `${initials}${branchCode}${randomDigits}`;
};

export const createBranch = async (
  bankId: string,
  data: Partial<BankClientData>,
  companyId?: string
): Promise<string> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  try {
    const branchesRef = collection(db, 'bankClient', uid, 'banks', bankId, 'branches');

    // Generate a security code if not provided
    const securityCode = data.securityCode || `BNK-${Math.floor(1000 + Math.random() * 9000)}`;

    // Get bank name to generate pin code
    let bankName = data.bankName || '';
    if (!bankName) {
      const bankRef = doc(db, 'bankClient', uid, 'banks', bankId);
      const bankSnap = await getDoc(bankRef);
      if (bankSnap.exists()) {
        bankName = bankSnap.data().name || '';
      }
    }

    // Generate pin code if not provided
    const pinCode = data.pinCode || generatePinCode(bankName, data.branchCode || '');

    const branchData = {
      ...data,
      securityCode,
      pinCode,
      dateOfRegistration: data.dateOfRegistration || new Date(),
      requiredGuard: data.requiredGuard || 3,
      salary: data.salary || 0,
      salaryHistory: data.salary ? [{
        amount: data.salary,
        date: new Date(),
        type: 'set',
      } as SalaryHistoryEntry] : [],
      type: 'bank',
      companyId: uid,
      createdAt: new Date(),
    };

    const docRef = await addDoc(branchesRef, branchData);

    // Save security code reference
    const securityCodeRef = doc(collection(db, 'securityCodes'));
    await setDoc(securityCodeRef, {
      companyId: uid,
      clientType: 'Bank',
      clientId: docRef.id,
      bankId: bankId,
      branchId: docRef.id,
      securityCode,
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating branch:', error);
    throw error;
  }
};

export const fetchBankClients = async (companyId?: string): Promise<BankClientData[]> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  try {
    // Fetch all banks
    const banksSnap = await getDocs(collection(db, 'bankClient', uid, 'banks'));

    // Fetch locations
    const locations = await fetchLocations();

    const allBranches: BankClientData[] = [];

    // Iterate through each bank and fetch its branches
    for (const bankDoc of banksSnap.docs) {
      const bankData = bankDoc.data();
      const bankId = bankDoc.id;

      // Fetch branches for this bank
      const branchesSnap = await getDocs(
        collection(db, 'bankClient', uid, 'banks', bankId, 'branches')
      );

      branchesSnap.forEach((branchDoc) => {
        const branchData = branchDoc.data();
        const location = locations.find((loc) => loc.id === branchData.locationId);

        allBranches.push({
          id: branchDoc.id,
          bankId: bankId,
          branchId: branchDoc.id,
          bankName: bankData.name || '',
          name: bankData.name || '',
          branchCode: branchData.branchCode || '',
          mobile: branchData.mobile || '',
          address: branchData.address || '',
          dateOfRegistration: branchData.dateOfRegistration || null,
          locationId: branchData.locationId || '',
          locationName: location?.name || 'N/A',
          securityCode: branchData.securityCode || '',
          requiredGuard: branchData.requiredGuard || 3,
          psoName: branchData.psoName || '',
          psoEmail: branchData.psoEmail || '',
          psoNumber: branchData.psoNumber || '',
          bomName: branchData.bomName || '',
          bomEmail: branchData.bomEmail || '',
          pinCode: branchData.pinCode || '',
          salary: branchData.salary || 0,
          salaryHistory: branchData.salaryHistory || [],
          otGuardCount: branchData.otGuardCount || 0,
          type: 'bank',
        });
      });
    }

    return allBranches;
  } catch (error) {
    console.error('Error fetching bank clients:', error);
    throw error;
  }
};

export const updateBankClient = async (
  branchId: string,
  bankId: string,
  data: Partial<BankClientData>,
  companyId?: string
): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  try {
    const { bankName, locationName, branchId: _, id: __, ...updateData } = data;
    const branchRef = doc(db, 'bankClient', uid, 'banks', bankId, 'branches', branchId);

    // If salary is being updated, record history
    if (data.salary !== undefined) {
      const branchSnap = await getDoc(branchRef);
      if (branchSnap.exists()) {
        const currentData = branchSnap.data();
        const currentSalary = currentData.salary || 0;

        if (currentSalary !== data.salary) {
          const type = data.salary > currentSalary ? 'increase' : 'decrease';
          const historyEntry: SalaryHistoryEntry = {
            amount: data.salary,
            previousAmount: currentSalary,
            date: new Date(),
            type: type,
          };
          updateData.salaryHistory = arrayUnion(historyEntry) as any;
        }
      }
    }

    await updateDoc(branchRef, updateData);

    // NOTE: We intentionally do NOT update deployed guards' salaries here.
    // Guards should keep their original deployment salary for accurate historical calculations.
    // New deployments will pick up the new salary from the client document.
  } catch (error) {
    console.error('Error updating bank client:', error);
    throw error;
  }
};

export const formatDate = (date: Date | any): string => {
  if (!date) return '—';
  try {
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString();
    }
    return '—';
  } catch {
    return '—';
  }
};

/**
 * Migration: Add pin codes to all existing branches that don't have one
 */
export const migrationAddPinCodes = async (companyId?: string): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('Company ID or user authentication required');

  try {
    const clients = await fetchBankClients(uid);
    for (const client of clients) {
      if (!client.pinCode) {
        const pinCode = generatePinCode(client.bankName, client.branchCode || '');
        await updateBankClient(client.branchId, client.bankId, { pinCode }, uid);
        console.log(`Generated pin code for ${client.bankName} - ${client.branchCode}: ${pinCode}`);
      }
    }
  } catch (error) {
    console.error('Migration error:', error);
    throw error;
  }
};

